(ns frontend.view.reports.custom.alj-2.active-deals-per-deadline-to-close-bar-chart
  (:require
   [common.localization :as loc]
   [common.util :as cu]
   [reagent.core :as r]
   [frontend.view.reports.custom.alj-2.alj-components :as alj-components]
   [frontend.view.reports.custom.alj-2.utils :as alj-utils]
   [frontend.view.reports.custom.alj :as alj]
   [common.dates :as dates]
   [clojure.string :as string]
   [frontend.db :as app-db]))

(defn start-of-month-millis
  [dt]
  (let [dt (dates/start-of-month dt)]
    (- (.getTime dt)
       (* 1000 60 (.getTimezoneOffset dt)))))

(defn end-of-month-millis
  [dt]
  (let [dt (dates/end-of-month dt)]
    (- (.getTime dt)
       (* 1000 60 (.getTimezoneOffset dt)))))

(defn start-of-q-millis
  [dt]
  (let [q-start-month (dates/plus dt
                                  (dates/months
                                   (case (mod (inc (.getMonth dt)) 3)
                                     0 -2
                                     1 0
                                     2 -1)))]
    (start-of-month-millis q-start-month)))

(defn end-of-q-millis
  [dt]
  (let [months-till-end-of-q (case (mod (inc (.getMonth dt)) 3)
                               0 0
                               1 2
                               2 1)
        q-end-month (dates/plus dt
                                (dates/months months-till-end-of-q))]
    (end-of-month-millis q-end-month)))

(defn date->quarter
  [dt]
  (let [month (inc (.getMonth dt))
        Q (-> month
              (/ 3)
              (double)
              (Math/ceil)
              (int))]
    (str Q "Q" (.getYear dt))))

(defn periods-months-until-end-of-next-q-and-2-quarters
  []
  (let [now (dates/date)
        current-month (inc (.getMonth now))
        number-of-additional-months-to-show (case (mod current-month 3)
                                              0 4
                                              1 6
                                              2 5)]
    (concat [[(dates/format now {:pattern "MMM yyyy"})
              (.getTime (js/Date.))
              (end-of-month-millis now)]]
            (doall (for [months-from-now (range 1 number-of-additional-months-to-show)
                         :let [dt (dates/plus now (dates/months months-from-now))]]
                     [(dates/format dt {:pattern "MMM yyyy"})
                      (start-of-month-millis dt)
                      (end-of-month-millis dt)]))
            [(let [second-quarter-from-now (dates/plus now (dates/months number-of-additional-months-to-show))]
               [(date->quarter second-quarter-from-now)
                (start-of-q-millis second-quarter-from-now)
                (end-of-q-millis second-quarter-from-now)])
             (let [third-quarter-from-now (dates/plus now (dates/months (+ 3 number-of-additional-months-to-show)))]
               [(date->quarter third-quarter-from-now)
                (start-of-q-millis third-quarter-from-now)
                (end-of-q-millis third-quarter-from-now)])])))

(defn bar-deals-per-deadline-to-close
  [deals reports-state]
  (let [periods-to-report (periods-months-until-end-of-next-q-and-2-quarters)]
    [{:series
      [{:name (loc/tr :oppo/tag-segment)
        :data (->> periods-to-report
                   (map (fn [[period-name start-millis end-millis]]
                          (let [matching-deals (->> deals
                                                    (filter (fn [oppo]
                                                              (when-let [deadline-to-close (:oppo/datetime-alj-deadline-to-close-deal oppo)]
                                                                (> end-millis deadline-to-close start-millis)))))]
                            {:name    period-name
                             :y       (count matching-deals)
                             :dealIds (->> matching-deals
                                           (map :db/id)
                                           (doall))})))
                   (doall))}]
      :plotOptions
      {:series {:cursor "pointer"
                :point  {:events {:click (fn [e]
                                           (this-as js-data-point
                                                    (let [category (aget js-data-point "name")
                                                          category-deals (aget js-data-point "dealIds")]
                                                      (swap! reports-state assoc
                                                        :deadline-drilldown
                                                        {:label    category
                                                         :oppo-ids (js->clj category-deals)})
                                                      (deref reports-state))))}}}}}]))

(defn chart-width
  []
  (let [window-width (:width @app-db/window)
        paddings-and-margins-when-2-charts-per-row 300
        paddings-and-margins-when-single-item 210
        res (if (< window-width 900)
              (- window-width paddings-and-margins-when-single-item)
              (min (int (/ (- window-width paddings-and-margins-when-2-charts-per-row) 3))
                   1000))]
    res))

(defn report
  [{:keys [state reports-state]}]
  (let [date-state-atom (alj-utils/date-state-defaults
                         state
                         [[:date-created #{:entity.type/oppo} [:entity.type/oppo]]])]
    (fn [{:keys [state]}]
      (let [active-deals (->> (alj/all-deals)
                              (cu/filter-by :oppo/status :oppo.status/ok)
                              (alj-utils/deals-in-date-range-if-configured :common/created-on @date-state-atom :date-created :entity.type/oppo))
            [chart-data other-names] (bar-deals-per-deadline-to-close active-deals reports-state)
            first-non-empty-data (->> chart-data
                                      :series
                                      (first)
                                      :data
                                      (keep (fn [{:keys [name dealIds]}]
                                              (when (seq dealIds)
                                                {:label    name
                                                 :oppo-ids dealIds})))
                                      (first))
            export-data (->> chart-data
                             :series
                             (first)
                             :data
                             (map (fn [{:keys [name y]}]
                                    [name y]))
                             (doall))
            view-name (loc/tr :total-active-deals-by-deadline-to-close)]
        (when (and (not (:deadline-drilldown @reports-state))
                   first-non-empty-data)
          (swap! reports-state assoc :deadline-drilldown first-non-empty-data))
        [alj-components/alj-bar-chart
         {:view-name       view-name
          :total-deals     (count active-deals)
          :report-data     export-data
          :chart-data      chart-data
          :width           (chart-width)
          :date-state-atom date-state-atom
          :footer-content  (when other-names
                             [:ol.pl-4.pb-4.mr-4.ml-4
                              [:li.text-size--sm "Others include: "
                               (string/join ", " other-names)]])}]))))
